import { useEffect, useId, useRef } from 'react'
import { useAcoesDisponiveis } from '../../../api/queries/gmuds'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Carregando, EstadoVazio } from '../../../components/Estados'
import { TEXTOS } from '../../../lib/glossario'
import type { ResumoGmud, StatusGmud } from '../../../types/gmud'
import { ehDestrutiva } from '../detalhe/acoesDestrutivas'
import { DialogoDeConfirmacao } from '../detalhe/DialogoDeConfirmacao'
import { DialogoDeMotivo } from '../detalhe/DialogoDeMotivo'
import { useAcionarTransicao } from '../detalhe/useAcionarTransicao'
import { acaoParaZona, zonaAceitaSoltar } from './arraste'
import { QUADRO } from './textosDoQuadro'

interface Props {
  gmud: ResumoGmud
  /** O estado da zona em que o cartão foi solto, quando o menu abriu por arraste. */
  destinoProposto: StatusGmud | null
  /** Como essa zona se chama na tela, para dizer por extenso que ela não é destino. */
  nomeDaZona: string | null
  aoAnunciar: (texto: string) => void
  /** Chamado quando o servidor confirma a transição — o menu fecha e a proposta do arraste morre. */
  aoConcluir: () => void
}

/**
 * O que dá para fazer com este cartão — a mesma lista, o mesmo hook e os mesmos diálogos do detalhe.
 *
 * **É por aqui que o arraste passa.** Soltar um cartão numa zona não executa nada: apenas abre este
 * menu com um destino proposto, e o destino vira comando por `acaoParaZona`, que procura na resposta
 * do SERVIDOR. Não há mapa local de coluna para comando — ele seria a cópia divergente da matriz.
 * Se a zona não for destino possível, o menu abre mesmo assim, diz isso por extenso, e o cartão
 * continua onde estava.
 *
 * **O cartão nunca se move no otimismo.** Ele espera o servidor. Com 12 das 18 transições exigindo
 * justificativa, cancelar o diálogo é o caso comum — e cartão em limbo é pior que cartão parado.
 *
 * A consulta de ações é feita SOB DEMANDA, quando este componente monta. Carregá-la para os 100
 * cartões do quadro seria uma consulta por cartão para um dado que quase nenhum deles vai usar.
 *
 * @param props o cartão, o destino proposto pelo arraste e a região de anúncios
 * @returns o menu de ações
 */
export function AcoesDoCartao({ gmud, destinoProposto, nomeDaZona, aoAnunciar, aoConcluir }: Props) {
  const { data: acoes, isPending, isError, error } = useAcoesDisponiveis(gmud.id)
  const transicao = useAcionarTransicao(gmud.id)
  const base = useId()
  const propostaAtendida = useRef<StatusGmud | null>(null)
  const desfechoAnunciado = useRef(false)
  const { acionar, concluida, falhou, ultimaAcao } = transicao

  // O arraste vira proposta uma vez só: as ações chegaram, o destino é traduzido em comando, e daí
  // em diante quem manda é o diálogo. Sem a trava, cada novo desenho da tela reabriria o diálogo.
  useEffect(() => {
    if (!acoes || !destinoProposto || propostaAtendida.current === destinoProposto) {
      return
    }
    propostaAtendida.current = destinoProposto
    const acao = acaoParaZona(acoes, destinoProposto)
    if (acao && acao.habilitado) {
      acionar(acao)
      return
    }
    aoAnunciar(QUADRO.zonaNaoEhDestino(nomeDaZona ?? ''))
  }, [acoes, destinoProposto, nomeDaZona, acionar, aoAnunciar])

  // O desfecho é anunciado por extenso porque o cartão mudar de lugar não é percebido por quem usa
  // leitor de tela — e, quando o servidor recusa, ele não muda de lugar nenhum.
  useEffect(() => {
    if (!concluida && !falhou) {
      desfechoAnunciado.current = false
      return
    }
    if (desfechoAnunciado.current || !ultimaAcao) {
      return
    }
    desfechoAnunciado.current = true
    if (!concluida) {
      aoAnunciar(QUADRO.transicaoRecusada(gmud.codigo))
      return
    }
    aoAnunciar(QUADRO.transicaoConcluida(gmud.codigo, ultimaAcao.statusDestinoRotulo))
    // A proposta do arraste morre aqui. Sem isto, o cartão que acabou de mudar de coluna reabriria
    // o menu na coluna nova propondo de novo o destino já cumprido — o gesto valeria por dois.
    aoConcluir()
  }, [concluida, falhou, ultimaAcao, gmud.codigo, aoAnunciar, aoConcluir])

  if (isPending) {
    return <Carregando texto="Verificando o que você pode fazer…" />
  }
  if (isError) {
    return <AvisoDeErro erro={error} titulo="Não foi possível carregar as ações disponíveis" />
  }
  if (acoes.length === 0) {
    return <EstadoVazio titulo={TEXTOS.semAcoes.titulo} ajuda={TEXTOS.semAcoes.ajuda} />
  }

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {acoes.map((acao) => (
        <div key={acao.comando}>
          <Botao
            tamanho="pequeno"
            aparencia={ehDestrutiva(acao.comando) ? 'perigo' : 'secundario'}
            className="w-full"
            disabled={!acao.habilitado || transicao.executando}
            aria-describedby={acao.impedimentos.length > 0 ? `${base}-${acao.comando}` : undefined}
            onClick={() => acionar(acao)}
          >
            {acao.rotulo}
          </Botao>
          {acao.impedimentos.length > 0 && (
            <ul id={`${base}-${acao.comando}`} className="mt-1 space-y-0.5 text-xs text-slate-600">
              {acao.impedimentos.map((impedimento) => (
                <li key={impedimento}>{impedimento}</li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {/* Só quando há de fato uma decisão de comitê na lista: explicar a ausência do gesto num
          cartão onde ela não é oferecida seria ruído em toda coluna. */}
      {acoes.some((acao) => !zonaAceitaSoltar(acao.statusDestino)) && (
        <p className="text-xs text-slate-600">{QUADRO.decisaoDoComiteSaiDoMenu}</p>
      )}

      {transicao.falhou && !transicao.aguardandoMotivo && !transicao.aguardandoConfirmacao && (
        <AvisoDeErro erro={transicao.erro} titulo="A ação não foi concluída" />
      )}

      {transicao.aguardandoMotivo && (
        <DialogoDeMotivo
          acao={transicao.aguardandoMotivo}
          destrutiva={ehDestrutiva(transicao.aguardandoMotivo.comando)}
          enviando={transicao.executando}
          erro={transicao.falhou ? transicao.erro : null}
          aoConfirmar={transicao.confirmarComMotivo}
          aoCancelar={transicao.cancelar}
        />
      )}

      {transicao.aguardandoConfirmacao && (
        <DialogoDeConfirmacao
          acao={transicao.aguardandoConfirmacao}
          enviando={transicao.executando}
          erro={transicao.falhou ? transicao.erro : null}
          aoConfirmar={transicao.confirmar}
          aoCancelar={transicao.cancelar}
        />
      )}
    </div>
  )
}
